/**
 * PendingDiffBar — sticky action bar shown above the input box whenever
 * there's a pending diff awaiting user approval.
 *
 * Why it exists: long assistant text + diff card pushed Apply / Reject
 * buttons off-screen, forcing users to scroll to confirm changes. The
 * inline DiffPreview still renders for review (checkboxes, change list,
 * before/after) — but the action buttons also surface here at viewport
 * bottom so they're always one click away.
 *
 * Behavior:
 *   - Only renders when a message in the transcript has diff.status ===
 *     'pending' AND at least one change is selected. Nothing shown
 *     during streaming (the model is still proposing) or after applied/
 *     rejected (user already decided).
 *   - Apply / Reject reuse the same handlers wired in DiffPreview by
 *     dispatching to chatStore + the office.ts applier — no duplicate
 *     logic, just a second mount point of the same actions.
 *   - Hidden by default; pure CSS slide-up on appearance keeps it calm.
 */

import { useMemo } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useChatStore } from '@/stores/chatStore';
import { applyChanges, isCellEditingModeError } from '@/services/office';
import { confirmChanges } from '@/services/chat';
import { extractErrorMessage } from '@/services/api';
import type { ChangeDecision, DiffStatus, UIMessage } from '@/types/chat';

interface Props {
  conversationId: string | null;
}

// findActiveDiffMessage walks newest-first and returns the most recent
// message whose diff is awaiting user action. Streaming diffs are
// excluded — the model may still merge / drop entries before the final
// set arrives.
function findActiveDiffMessage(messages: UIMessage[]): UIMessage | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m.diff) continue;
    if (m.diff.status === 'pending' || m.diff.status === 'applying') return m;
  }
  return null;
}

export function PendingDiffBar({ conversationId }: Props) {
  const { messages, setDiffStatus } = useChatStore();
  const message = useMemo(() => findActiveDiffMessage(messages), [messages]);

  if (!message || !message.diff) return null;

  const { status, changes, selected } = message.diff;
  const selectedCount = changes.filter((c) => selected[c.id]).length;
  const isApplying = status === 'applying';

  const onApply = async () => {
    if (!conversationId) {
      toast.error('Conversation not saved yet; cannot apply.');
      return;
    }
    const toApply = changes.filter((c) => selected[c.id]);
    if (toApply.length === 0) return;

    setDiffStatus(message.id, 'applying');
    try {
      const { appliedIds, failed } = await applyChanges(toApply);
      const decisions: ChangeDecision[] = changes.map((c) => {
        if (!selected[c.id]) return { change_id: c.id, outcome: 'rejected' };
        if (appliedIds.includes(c.id)) return { change_id: c.id, outcome: 'approved' };
        return { change_id: c.id, outcome: 'rejected' };
      });
      try {
        await confirmChanges({
          conversation_id: conversationId,
          message_id: message.id,
          decisions,
        });
      } catch (err) {
        console.warn('confirmChanges failed:', extractErrorMessage(err));
      }
      const finalStatus: DiffStatus =
        failed.length === 0 ? 'applied' : appliedIds.length === 0 ? 'rejected' : 'partial';
      setDiffStatus(message.id, finalStatus, appliedIds);
      if (failed.length > 0) {
        const first = failed[0];
        if (isCellEditingModeError(first.reason)) {
          toast.error('Excel cell edit mode me hai. Empty cell pe click karke retry karo.');
        } else {
          const reason = first.reason ? ` — ${first.reason}` : '';
          toast.error(`${failed.length} change(s) could not be applied${reason}`);
        }
      } else {
        toast.success(`Applied ${appliedIds.length} change(s) — Ctrl+Z to undo`);
      }
    } catch (err) {
      setDiffStatus(message.id, 'rejected');
      toast.error(extractErrorMessage(err, 'Apply failed'));
    }
  };

  const onRejectAll = async () => {
    if (!conversationId) {
      setDiffStatus(message.id, 'rejected', []);
      return;
    }
    const decisions: ChangeDecision[] = changes.map((c) => ({
      change_id: c.id,
      outcome: 'rejected',
    }));
    setDiffStatus(message.id, 'applying');
    try {
      await confirmChanges({
        conversation_id: conversationId,
        message_id: message.id,
        decisions,
      });
    } catch (err) {
      console.warn('confirmChanges failed:', extractErrorMessage(err));
    }
    setDiffStatus(message.id, 'rejected', []);
  };

  return (
    <div className="border-t border-primary/30 bg-primary/5 px-3 py-2 dark:bg-primary/10 animate-in fade-in slide-in-from-bottom-2 duration-200">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          <span className="font-medium">
            {changes.length} change{changes.length === 1 ? '' : 's'} ready
          </span>
          {selectedCount !== changes.length && (
            <span className="text-text-secondary">
              · {selectedCount} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRejectAll}
            disabled={isApplying}
            className="btn-ghost text-xs px-2 py-1"
          >
            <X className="mr-1 h-3.5 w-3.5" /> Reject
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={isApplying || selectedCount === 0}
            className="btn-primary text-xs px-3 py-1.5"
          >
            {isApplying ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="mr-1 h-3.5 w-3.5" />
            )}
            Apply ({selectedCount})
          </button>
        </div>
      </div>
    </div>
  );
}
